// R109 #a (Jesse: "an issue that was reported as resolved but no way to really
// verify that"): when a ground-team issue is marked fixed / resolved, ask for
// an AFTER photo — or a reason there isn't one. Uses the .iss-modal-wrap class
// so the 15-minute auto-refresh never wipes a half-taken photo.
//
// Resolves to { files } (after photos picked), { skip_reason } or null (cancel).
//
// R116 #5 (Jorge: "some issues or resolutions are impossible to show in a
// picture"): photos OR a video, several at once. The caller queues them (the
// resolve never waits for the upload); a clip over the server's size cap is
// flagged here, before anything is sent.
import { EVIDENCE_ACCEPT, isVideoFile, pickSummary, fmtBytes } from '../lib/mediaKind';
import { uploadCapBytes, loadUploadCap } from '../../lib/photoUploader';
export type AfterPhotoResult = { files: File[]; skip_reason?: undefined } | { files?: undefined; skip_reason: string } | null;

const SKIP_REASONS = ['Fixed before I could take a photo', "Can't show it in a photo (sound, smell, Wi-Fi…)", 'Phone / camera problem', 'Not something we fixed — nothing to show'];

const esc = (s: any) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

export function askAfterPhoto(opts: { title?: string; verb?: string } = {}): Promise<AfterPhotoResult> {
  return new Promise(resolve => {
    const wrap = document.createElement('div');
    wrap.className = 'iss-modal-wrap';
    wrap.style.cssText = 'position:fixed; inset:0; background:rgba(15,23,42,.5); z-index:9700; display:flex; align-items:center; justify-content:center; padding:12px;';
    wrap.innerHTML = `
      <div style="background:var(--card,#fff); border-radius:14px; padding:18px 20px; max-width:440px; width:100%; box-shadow:0 8px 28px rgba(15,23,42,.18); display:flex; flex-direction:column; gap:12px; font-size:13px;">
        <div style="font-size:15px; font-weight:800;">📷 After photo or video${opts.title ? ` <span style="font-weight:400; color:var(--text-muted);">· ${esc(String(opts.title).slice(0, 60))}</span>` : ''}</div>
        <div style="color:var(--text-muted);">Show it's fixed — photos, or a short video when a picture can't show it. They sit next to the "before" so anyone can check, and keep uploading after you close this.</div>
        <label style="display:flex; flex-direction:column; align-items:center; justify-content:center; gap:2px; min-height:56px; padding:12px 14px; box-sizing:border-box; border:2px dashed var(--accent-border,#bfd7fe); border-radius:12px; cursor:pointer; font-weight:700; font-size:14px; color:var(--accent,#2563eb); background:var(--accent-soft,#eaf2ff); text-align:center;">
          📷 Take / choose photos or video
          <span style="font-weight:500; font-size:11.5px; color:var(--text-muted);">Pick several at once</span>
          <input type="file" accept="${EVIDENCE_ACCEPT}" multiple data-ap-file style="display:none;">
        </label>
        <div data-ap-picked role="status" aria-live="polite" style="display:none; color:var(--success-text,#166534); font-weight:600;"></div>
        <details data-ap-skipbox>
          <summary style="cursor:pointer; color:var(--text-muted);">No photo? Say why</summary>
          <div style="display:flex; flex-direction:column; gap:6px; margin-top:8px;">
            ${SKIP_REASONS.map((r, i) => `<label style="display:flex; gap:8px; align-items:center; cursor:pointer;"><input type="radio" name="ap-skip" value="${esc(r)}" ${i === 0 ? '' : ''}> ${esc(r)}</label>`).join('')}
            <label style="display:flex; gap:8px; align-items:center; cursor:pointer;"><input type="radio" name="ap-skip" value="__other"> Other: <input type="text" data-ap-other maxlength="160" style="flex:1; padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-size:12.5px;"></label>
          </div>
        </details>
        <div style="display:flex; gap:8px; justify-content:flex-end;">
          <button data-ap-cancel style="padding:8px 14px; border:1px solid var(--border); border-radius:8px; background:var(--card,#fff); cursor:pointer;">Cancel</button>
          <button data-ap-go style="padding:8px 14px; border:1px solid #166534; border-radius:8px; background:#166534; color:#fff; font-weight:700; cursor:pointer;" disabled>${esc(opts.verb || 'Mark fixed')}</button>
        </div>
      </div>`;
    document.body.appendChild(wrap);
    let files: File[] = [];
    const go = wrap.querySelector('[data-ap-go]') as HTMLButtonElement;
    const skipVal = (): string => {
      const pick = wrap.querySelector('input[name="ap-skip"]:checked') as HTMLInputElement | null;
      if (!pick) return '';
      if (pick.value === '__other') return ((wrap.querySelector('[data-ap-other]') as HTMLInputElement).value || '').trim();
      return pick.value;
    };
    const refresh = () => { go.disabled = !(files.length || skipVal()); };
    const done = (v: AfterPhotoResult) => { wrap.remove(); resolve(v); };
    void loadUploadCap();
    (wrap.querySelector('[data-ap-file]') as HTMLInputElement).addEventListener('change', (e) => {
      const picked = Array.from((e.currentTarget as HTMLInputElement).files || []);
      const cap = uploadCapBytes();
      const big = picked.filter(f => f.size > cap);
      files = picked.filter(f => f.size <= cap);
      const p = wrap.querySelector('[data-ap-picked]') as HTMLElement;
      p.style.display = picked.length ? '' : 'none';
      p.style.color = big.length ? 'var(--warning-text,#92400e)' : 'var(--success-text,#166534)';
      const ok = files.length ? `✓ ${pickSummary(files)} ready${files.some(isVideoFile) ? ' (the video keeps uploading after you close this)' : ''}` : '';
      const warn = big.length ? `"${big[0].name}" is ${fmtBytes(big[0].size)} — the limit is ${fmtBytes(cap)}; record a shorter clip (about 2 min).` : '';
      p.textContent = [ok, warn].filter(Boolean).join(' · ');
      refresh();
    });
    wrap.querySelectorAll('input[name="ap-skip"]').forEach(el => el.addEventListener('change', refresh));
    (wrap.querySelector('[data-ap-other]') as HTMLInputElement).addEventListener('input', () => {
      const o = wrap.querySelector('input[name="ap-skip"][value="__other"]') as HTMLInputElement; o.checked = true; refresh();
    });
    wrap.querySelector('[data-ap-cancel]')?.addEventListener('click', () => done(null));
    wrap.addEventListener('click', (e) => { if (e.target === wrap) done(null); });
    go.addEventListener('click', () => {
      if (files.length) return done({ files });
      const why = skipVal();
      if (why) return done({ skip_reason: why });
    });
  });
}

// Ask once for a whole batch (bulk / parent resolve): a reason that covers
// every row resolved without its own after photo. null = cancel.
export function askBatchSkipReason(n: number): string | null {
  const r = window.prompt(`Resolve ${n} issue${n === 1 ? '' : 's'} without after photos — why? (e.g. "checked in person on the walkthrough")`, '');
  if (r == null) return null;
  return r.trim() || 'resolved in bulk';
}
